import React, {useState, useEffect, useRef} from 'react';
import {Button, Form, Input, Select, Modal, Row, Col, Space, Tag, Checkbox} from 'antd';
import {DownOutlined, SearchOutlined, UpOutlined} from '@ant-design/icons';
import Table from "@/biz/Table.jsx";
import useHTTP from "@/hooks/useHTTP.mjs";
import runAsync from '@/script/runAsync';
import BizNodeTypeTag, {
    convertToBizNodeTypeName,
    convertToDppSubTypeName,
    bizNodeTypeOptions
} from "@/components/BizNodeTypeTag/BizNodeTypeTag";
import buildUrl from '@/script/buildUrl.mjs';
import {openDetail} from '@/script/DpUtils.mjs';

/**
 * SelectModuleDialog ：选择模件【单选】
 *
 * 使用场景：
 *
 * 参数说明：
 * visible:     【必填】 当前Dialog是否展示
 * setVisible:  【必填】 当前Dialog展示的操作方法
 * onCall:      【必填】 回调方法
 * isSingle:    【可选】 true(单选、默认)  false(多选)
 * title:       【可选】 弹窗标题
 *
 * @param {*} param
 * @returns 单选，返回对象；多选返回数组；
 **/
const SelectModuleDialog = ({
                                visible,
                                onOk,
                                onCancel,
                                isSingle = true,
                                prjId,
                                sVer,
                                filterModuleName,
                                filterModuleIds,
                                filterBizNodeTypes,
                                title = "选择模件",
                                ...props
                            }) => {
    const http = useHTTP();

    // 表单
    const [form] = Form.useForm();

    // 数据模型
    const [modules, setModules] = useState([]);

    // 加载显示
    const [loading, setLoading] = useState(false);
    const [expand, setExpand] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]); // 选中的对象集合

    // 组件初始化
    useEffect(() => {
        if (visible) {
            form.setFieldsValue({
                moduleName: filterModuleName,
                bizNodeTypes: filterBizNodeTypes ?? []
            });
            setModules([]);
            setSelectedRows([]); // 处理被选中的项目，当主Dialog关闭，需要清空选择的数据
            doSearch();
        }
    }, [visible]);

    const doSearch = async () => {
        try {
            setLoading(true);
            const formField = await form.getFieldsValue();
            formField.prjId = prjId;
            formField.sVer = sVer;
            if (formField.bizNodeTypes && formField.bizNodeTypes.length > 0) {
                const bizNodeTypes = new Set();
                for (let i = 0; i < formField.bizNodeTypes.length; i++) {
                    let bizNodeType = formField.bizNodeTypes[i];
                    if (bizNodeType) {
                        const types = bizNodeType.split(/,+/).filter(Boolean);
                        types.forEach(type => bizNodeTypes.add(type));
                    }
                }
                formField.bizNodeTypes = Array.from(bizNodeTypes);
            }
            let data = [];
            if (filterModuleIds && filterModuleIds.length > 0) {
                data = await runAsync(
                    async () => {
                        return await http.post(`/dps/module/selList`, {
                            ...formField,
                            filterModuleIds
                        });
                    }, {
                        error: {content: (err) => `查询失败: ${err.message}`},
                        loading: {show: true},
                        success: {show: false},
                    });
            } else if (!filterModuleIds) {
                data = await runAsync(
                    async () => {
                        return await http.post(`/dps/module/selList`, {
                            ...formField,
                        });
                    }, {
                        error: {content: (err) => `查询失败: ${err.message}`},
                        loading: {show: true},
                        success: {show: false},
                    });
            }

            if (data && data.length > 0) {
                data.forEach(item => {
                    // 将制品版本显示到模型版本
                    if (item.dppId == null || item.dppId == undefined) {
                        item.dppRev = item.dpRev
                    }

                    item.dpTypeName = convertToBizNodeTypeName(item.dppbizNodeType ?? item.dpbizNodeType, item.dpType)

                    if (item.dppId == null || item.dppId == undefined) {
                        item.dpSnShow = item.dpSn;

                        item.dppNameShow = item.dpName + (item.dpNo ? '/' + item.dpNo : '');

                        item.dpNameShow = null;
                        item.dpRevShow = null;

                    } else {
                        item.dpSnShow = item.dppSn;
                        item.dppNameShow = '【' + item.dpName + '-V' + item.sVer + '.' + item.dpRev + '】' + item.dppName + (item.dppNo ? '/' + item.dppNo : '');

                        item.dpNameShow = item.dpName;
                        item.dpRevShow = item.dpRev;
                    }

                    item.dppRevShow = item.sVer + "." + item.dppRev;
                });
            }
            setModules(data);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    const doReset = async () => {
        form.resetFields();
        doSearch();
        setSelectedRows([]);
    }

    // 确认回调
    const doConfirm = async () => {
        if (isSingle) {
            if (selectedRows.length == 0) {
                onOk(null);
            } else {
                const data = selectedRows[0];
                onOk(data);
            }
        } else {
            onOk(selectedRows);
        }
    }

    const handleTableRowClick = (row) => {
        if (isSingle) {
            onOk(row)
        } else {
            onOk([row]);
        }
    }

    // 关闭Dialog
    const doClose = () => {
        form.resetFields();
        setSelectedRows([]);
        onCancel();
    }

    const rowSelection = {
        type: isSingle ? 'radio' : 'checkbox',
        selectedRowKeys: selectedRows.map(({pkid}) => pkid),
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRows(selectedRows);
        },
    }

    const columns = [
        {
            title: '模件类型',
            dataIndex: 'bizNodeTypeName',
            width: 200,
            ellipsis: true,
            getValue: record => {
                const bizNodeTypeName = convertToBizNodeTypeName(record.dppBizNodeType ?? record.dpBizNodeType, record.dpType)
                const dppSubTypeName = convertToDppSubTypeName(record.dppBizNodeType, record.dppType);
                return bizNodeTypeName + (dppSubTypeName ? ' / ' + dppSubTypeName : '');
            },
            filter: <Table.FilterText/>,
            render: (text, record) => {

                return <BizNodeTypeTag bizNodeType={record.dppBizNodeType ?? record.dpBizNodeType}
                                       dpType={record.dppType ?? record.dpType}/>
            }
        },
        {
            title: '模件域',
            dataIndex: 'mName',
            width: 160,
            ellipsis: true,
            filter: <Table.FilterText />,
        },
        {
            title: '模件编号',
            dataIndex: 'dpSnShow',
            width: 200,
            ellipsis: true,
            filter: <Table.FilterText/>,
        },
        {
            title: '模件名称/代码',
            dataIndex: 'dppNameShow',
            width: 300,
            ellipsis: true,
            filter: <Table.FilterText/>,
            render: (text, record, index) => {

                const openVersion = (record) => {
                    let url;
                    if (record.dppId == null || record.dppId == undefined) {
                        url = buildUrl('/SoftwareMap', {id: record.mapVerId, mode: 'snapshot'});
                    } else {
                        const inits = {
                            selectedNodes: [{
                                data: {pkid: record.dppId},
                                isMounted: true,
                            }]
                        };
                        url = buildUrl('/SoftwareMap', {id: record.mapVerId, mode: 'snapshot', inits});
                    }
                    window.open(url);
                }

                // 制品、集成图
                if (record.dppId == null || record.dppId == undefined) {

                    const bizNodeType = record.dpBizNodeType == 'BD' ? (record.dpType ?? record.dpBizNodeType) : record.dpBizNodeType;
                    return (
                        <Space>
                            <a href="javascript:void(0);" rel="noreferrer"
                               onClick={() => openDetail(record.dpId, bizNodeType, null, null, record.prjId)}>{text}</a>
                        </Space>
                    );
                } else {
                    const bizNodeType = record.dpBizNodeType == 'BD' ? (record.dpType ?? record.dpBizNodeType) : record.dpBizNodeType;

                    return (
                        <Space>
                            <a href="javascript:void(0);" rel="noreferrer"
                               onClick={() => openDetail(record.dpId, bizNodeType, record.dppId, true)}>{text}</a>
                        </Space>
                    );
                }
            }
        },
        {
            title: '模件版本号',
            dataIndex: 'dppRevShow',
            width: 160,
            ellipsis: true,
            filter: <Table.FilterText/>,
        },
        {
            title: '操作',
            dataIndex: 'operation',
            width: 100,
            className: 'center',
            render: (text, record) => {
                return (
                    <a onClick={() => handleTableRowClick(record)}>
                        选择
                    </a>
                )
            }
        },
    ];

    const formCss = {
        '& .ant-form-item': {
            marginBottom: '12px'
        }
    }

    const dpTypeSearch = (inputValue, option) => {
        return option?.children?.toLowerCase().indexOf(inputValue?.toLowerCase()) >= 0 ? true : false;
    }

    return (
        <Modal
            open={visible}
            title={title}
            width={1500}
            onOk={doConfirm}
            onCancel={doClose}
            bodyStyle={{
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                height: '70vh',
                minHeight: 320,
                background: '#fff',
                padding: 12
            }}
        >
            <div style={{
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                padding: 0,
                gap: 12,
                flexGrow: 1
            }}>
                {/* 查询条件 */}
                <div style={{padding: '0px 12px', background: '#fff'}}>
                    <Form form={form} onFinish={() => doSearch()}>
                        <Row gutter={24}>
                            <Col span={4}>
                                <Form.Item name="bizNodeTypes" label="模件类型">
                                    <Select mode="multiple" showSearch={true} allowClear={true}
                                            filterOption={(inputValue, option) => dpTypeSearch(inputValue, option)}
                                    >
                                        {
                                            bizNodeTypeOptions().filter(item => item.dpFlag !== '2').map(({
                                                                                                              label,
                                                                                                              value
                                                                                                          }) => {
                                                return (
                                                    <Select.Option value={value} label={label}>
                                                        {label}
                                                    </Select.Option>
                                                )
                                            })
                                        }
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="moduleSn" label="模件编号">
                                    <Input allowClear/>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="moduleNo" label="模件代码">
                                    <Input allowClear/>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="moduleName" label="模件名称">
                                    <Input allowClear/>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="moduleDomainName" label="模件域">
                                    <Input allowClear/>
                                </Form.Item>
                            </Col>
                            <Col span={4} style={{ display: expand ? 'block' : 'none' }}>
                                <Form.Item name="moduleDpName" label="所属制品">
                                    <Input allowClear/>
                                </Form.Item>
                            </Col>

                            <Col span={4} offset={expand ? 20 : 0} style={{textAlign: 'right'}}>
                                <Button onClick={doReset} style={{margin: '0 8px'}}>重置</Button>
                                <Button type="primary" htmlType="submit" icon={<SearchOutlined/>}>查询</Button>

                                <a
                                    style={{fontSize: 12, marginLeft: '8px'}}
                                    onClick={() => {
                                        setExpand(!expand);
                                    }}
                                >
                                    {expand ? <UpOutlined/> : <DownOutlined/>} {expand ? '收起' : '展开'}
                                </a>
                            </Col>
                        </Row>
                    </Form>
                </div>

                {/* 数据表格 */}
                <div style={{
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    background: '#fff',
                    flexGrow: 1,
                }}>
                    <Table
                        size="middle"
                        loading={loading}
                        columns={columns}
                        dataSource={modules}
                        rowKey={"pkid"}
                        rowSelection={{type: "checkbox", ...rowSelection}}
                    />
                </div>
            </div>
        </Modal>
    );
}

export default SelectModuleDialog;